import { Injectable } from '@angular/core';
import { ApiService } from './api.service';

/**
 * SIMR (Single ID – Multiple Role): keeps the chosen linked account after full page reload.
 *
 * On refresh, existing bootstrap may call /auth/refresh and restore the primary user while
 * `simr_intended_user_id` still points at the last switched linked account.
 * This service re-calls switch-account so the session matches that user.
 *
 * Runs after normal bootstrap — does not replace existing auth logic.
 */
@Injectable({ providedIn: 'root' })
export class SimrSwitchAccountService {
  static readonly STORAGE_INTENDED_USER_ID = 'simr_intended_user_id';

  async reapplyIfNeeded(api: ApiService): Promise<void> {
    const intended = sessionStorage.getItem(SimrSwitchAccountService.STORAGE_INTENDED_USER_ID);
    if (!intended) {
      return;
    }

    const maxAttempts = 15;
    const delayMs = 300;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, delayMs));
      }

      let currentId: string | undefined;
      try {
        const raw = localStorage.getItem('current_user');
        if (raw) {
          currentId = JSON.parse(raw)?.id;
        }
      } catch {
        return;
      }

      if (!currentId) {
        continue;
      }

      if (currentId === intended) {
        return;
      }

      if (currentId !== intended) {
        try {
          console.log('[SimrSwitchAccount] Re-applying linked session', { intended, currentId, attempt });
          await api.switchAccount(intended);
        } catch (e) {
          console.error('[SimrSwitchAccount] reapplyIfNeeded failed, clearing stale intended user', e);
          // Clear stale intended user so it doesn't keep retrying on every page load
          sessionStorage.removeItem(SimrSwitchAccountService.STORAGE_INTENDED_USER_ID);
        }
        return;
      }
    }
  }
}
